/**
 * RISE & WEAR — Theme JavaScript
 * Anti-White-Screen: all init wrapped in shopify:section:load handlers
 */

/* ── Utilities ──────────────────────────────────────────── */
const rw = {
  on(el, event, handler, opts) {
    if (!el) return;
    el.addEventListener(event, handler, opts);
  },
  qs(selector, scope = document) {
    return scope.querySelector(selector);
  },
  qsa(selector, scope = document) {
    return Array.from(scope.querySelectorAll(selector));
  },
  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }
};

/* ── Scroll-based header ────────────────────────────────── */
function initHeader() {
  const header = rw.qs('.site-header');
  if (!header) return;

  let lastScroll = 0;
  let ticking = false;

  function onScroll() {
    lastScroll = window.scrollY;
    if (!ticking) {
      window.requestAnimationFrame(() => {
        if (lastScroll > 60) {
          header.classList.add('is-scrolled');
        } else {
          header.classList.remove('is-scrolled');
        }
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ── Hamburger Menu ─────────────────────────────────────── */
function initHamburger() {
  const btn = rw.qs('.hamburger-btn');
  const dropdown = rw.qs('.nav-dropdown');
  if (!btn || !dropdown) return;

  function open() {
    btn.classList.add('is-open');
    dropdown.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onOutsideClick, true);
  }

  function close() {
    btn.classList.remove('is-open');
    dropdown.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('click', onOutsideClick, true);
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  function onOutsideClick(e) {
    if (!btn.contains(e.target) && !dropdown.contains(e.target)) {
      close();
    }
  }

  rw.on(btn, 'click', () => {
    if (btn.classList.contains('is-open')) close(); else open();
  });
}

/* ── Search Overlay ─────────────────────────────────────── */
function initSearch() {
  const openBtn = rw.qs('.search-open-btn');
  const overlay = rw.qs('.search-overlay');
  const closeBtn = rw.qs('.search-close-btn');
  const input = rw.qs('.search-input');
  if (!openBtn || !overlay) return;

  function open() {
    overlay.classList.add('is-open');
    overlay.removeAttribute('aria-hidden');
    setTimeout(() => input && input.focus(), 100);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
  }

  function close() {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
  }

  function onKey(e) {
    if (e.key === 'Escape') close();
  }

  rw.on(openBtn, 'click', open);
  rw.on(closeBtn, 'click', close);
  rw.on(overlay, 'click', (e) => {
    if (e.target === overlay) close();
  });
}

/* ── Cart Drawer ────────────────────────────────────────── */
const CartDrawer = {
  drawer: null,
  overlay: null,
  countBadge: null,

  init() {
    this.drawer = rw.qs('.cart-drawer');
    this.overlay = rw.qs('.cart-overlay');
    this.countBadge = rw.qs('.cart-count-badge');
    if (!this.drawer) return;

    const closeBtn = rw.qs('.cart-close-btn');
    const cartTriggers = rw.qsa('[data-cart-trigger]');

    rw.on(closeBtn, 'click', () => this.close());
    rw.on(this.overlay, 'click', () => this.close());

    cartTriggers.forEach(btn => {
      rw.on(btn, 'click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    document.addEventListener('cart:updated', () => this.refresh());
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });

    this.fetchAndRender();
  },

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    this.drawer.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },

  setLoading(state) {
    const body = rw.qs('.cart-drawer-body', this.drawer);
    if (!body) return;
    if (state) {
      body.style.opacity = '0.5';
      body.style.pointerEvents = 'none';
    } else {
      body.style.opacity = '1';
      body.style.pointerEvents = '';
    }
  },

  async fetchAndRender() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.error('Cart fetch error:', e);
    }
  },

  render(cart) {
    const body = rw.qs('.cart-drawer-body', this.drawer);
    const subtotalEl = rw.qs('.cart-subtotal-amount', this.drawer);
    const countLabel = rw.qs('.cart-count-label', this.drawer);
    if (!body) return;

    const count = cart.item_count || 0;
    const subtotal = this.formatMoney(cart.total_price || 0);

    if (this.countBadge) {
      this.countBadge.textContent = count;
      this.countBadge.setAttribute('data-count', count);
    }
    if (subtotalEl) subtotalEl.textContent = subtotal;
    if (countLabel) countLabel.textContent = count === 1 ? '1 item' : `${count} items`;

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = `
        <div class="cart-empty-state">
          <svg class="cart-empty-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 16H4L5 9z"/>
          </svg>
          <p>Your bag is empty</p>
          <a href="/collections/all" class="btn btn-primary" style="font-size:12px;padding:12px 28px;">Browse Products</a>
        </div>`;
      return;
    }

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-line-key="${item.key}">
        <img
          class="cart-item-img"
          src="${item.featured_image ? item.featured_image.url : ''}"
          alt="${item.product_title}"
          width="80"
          height="96"
          loading="lazy"
        >
        <div class="cart-item-info">
          <p class="cart-item-title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}
          <div class="cart-item-quantity-row">
            <div class="qty-control">
              <button class="qty-btn" data-action="decrease" data-line="${item.key}" aria-label="Decrease quantity">−</button>
              <span class="qty-display">${item.quantity}</span>
              <button class="qty-btn" data-action="increase" data-line="${item.key}" aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-item-price">${this.formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item-remove" data-action="remove" data-line="${item.key}">Remove</button>
        </div>
      </div>
    `).join('');

    // Bind qty controls
    rw.qsa('.qty-btn', body).forEach(btn => {
      rw.on(btn, 'click', (e) => this.handleQtyChange(e));
    });
    rw.qsa('.cart-item-remove', body).forEach(btn => {
      rw.on(btn, 'click', (e) => this.handleRemove(e));
    });
  },

  async handleQtyChange(e) {
    const btn = e.currentTarget;
    const action = btn.getAttribute('data-action');
    const key = btn.getAttribute('data-line');
    const qtyEl = btn.closest('.qty-control').querySelector('.qty-display');
    if (!qtyEl) return;

    const currentQty = parseInt(qtyEl.textContent, 10);
    const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);

    this.setLoading(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  },

  async handleRemove(e) {
    const btn = e.currentTarget;
    const key = btn.getAttribute('data-line');
    this.setLoading(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  },

  async refresh() {
    this.setLoading(true);
    await this.fetchAndRender();
    this.setLoading(false);
  },

  formatMoney(cents) {
    return 'Rs.' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
};

/* ── Quick Add ──────────────────────────────────────────── */
function initQuickAdd() {
  // Touch start triggers the overlay without delay
  rw.qsa('.product-card').forEach(card => {
    rw.on(card, 'touchstart', () => {
      card.classList.add('touch-active');
    }, { passive: true });

    rw.on(card, 'touchend', () => {
      setTimeout(() => card.classList.remove('touch-active'), 2000);
    }, { passive: true });

    rw.on(card, 'touchcancel', () => {
      card.classList.remove('touch-active');
    }, { passive: true });
  });

  // Quick add button click
  rw.qsa('.quick-add-btn').forEach(btn => {
    rw.on(btn, 'click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span>';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        });
        if (!res.ok) throw new Error('Failed');

        btn.textContent = '✓ Added';
        btn.style.background = '#22c55e';
        btn.style.color = '#fff';

        rw.emit('cart:updated');
        rw.emit('cart:open');

        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.background = '';
          btn.style.color = '';
          btn.disabled = false;
        }, 2000);
      } catch (err) {
        btn.textContent = 'Error';
        btn.style.background = '#ef4444';
        btn.style.color = '#fff';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.background = '';
          btn.style.color = '';
          btn.disabled = false;
        }, 2000);
      }
    });
  });
}

/* ── Product Page Variant Select ────────────────────────── */
function initProductPage() {
  const form = rw.qs('.product-form');
  if (!form) return;

  const variantBtns = rw.qsa('.variant-btn', form);
  const priceEl = rw.qs('.product-price-display', document);
  const priceCompareEl = rw.qs('.product-price-compare', document);
  const atcBtn = rw.qs('.atc-btn--add', form);
  const variantInput = rw.qs('input[name="id"]', form);

  // Gallery thumbnails
  const mainImg = rw.qs('.product-gallery-main img');
  const thumbs = rw.qsa('.product-thumb');
  thumbs.forEach(thumb => {
    rw.on(thumb, 'click', () => {
      const src = thumb.getAttribute('data-src');
      if (mainImg && src) {
        mainImg.src = src;
      }
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });

  // Variant selector
  variantBtns.forEach(btn => {
    rw.on(btn, 'click', () => {
      const group = btn.closest('.variant-options');
      if (!group) return;
      rw.qsa('.variant-btn', group).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      // Update selected value label
      const label = btn.closest('.variant-selector-group')?.querySelector('.variant-selected-value');
      if (label) label.textContent = btn.textContent.trim();

      // Update variant ID
      const variantId = btn.getAttribute('data-variant-id');
      const available = btn.getAttribute('data-available') !== 'false';
      if (variantInput && variantId) variantInput.value = variantId;

      // Update price
      const price = btn.getAttribute('data-price');
      const comparePrice = btn.getAttribute('data-compare-price');
      if (priceEl && price) priceEl.textContent = CartDrawer.formatMoney(parseInt(price, 10));
      if (priceCompareEl && comparePrice && comparePrice !== '0') {
        priceCompareEl.textContent = CartDrawer.formatMoney(parseInt(comparePrice, 10));
        priceCompareEl.style.display = '';
      } else if (priceCompareEl) {
        priceCompareEl.style.display = 'none';
      }

      // Sold out
      if (atcBtn) {
        if (!available) {
          atcBtn.textContent = 'Sold Out';
          atcBtn.classList.add('atc-btn--sold-out');
          atcBtn.classList.remove('atc-btn--add');
          atcBtn.disabled = true;
        } else {
          atcBtn.textContent = 'Add to Bag';
          atcBtn.classList.remove('atc-btn--sold-out');
          atcBtn.classList.add('atc-btn--add');
          atcBtn.disabled = false;
        }
      }
    });
  });

  // ATC submit
  rw.on(form, 'submit', async (e) => {
    e.preventDefault();
    if (!atcBtn || atcBtn.disabled) return;

    const original = atcBtn.textContent;
    atcBtn.disabled = true;
    atcBtn.innerHTML = '<span class="spinner"></span>';

    const formData = new FormData(form);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity') || '1', 10)
        })
      });
      if (!res.ok) throw new Error('Failed');

      atcBtn.textContent = '✓ Added to Bag';
      atcBtn.style.background = '#22c55e';
      atcBtn.style.color = '#fff';
      rw.emit('cart:updated');
      rw.emit('cart:open');

      setTimeout(() => {
        atcBtn.textContent = original;
        atcBtn.style.background = '';
        atcBtn.style.color = '';
        atcBtn.disabled = false;
      }, 2500);
    } catch (err) {
      atcBtn.textContent = 'Error — Try Again';
      atcBtn.style.background = '#ef4444';
      atcBtn.style.color = '#fff';
      setTimeout(() => {
        atcBtn.textContent = original;
        atcBtn.style.background = '';
        atcBtn.style.color = '';
        atcBtn.disabled = false;
      }, 2000);
    }
  });

  // Buy Now
  const buyBtn = rw.qs('.atc-btn--buy', form);
  rw.on(buyBtn, 'click', async (e) => {
    e.preventDefault();
    if (!buyBtn || buyBtn.disabled) return;

    const variantInput = rw.qs('input[name="id"]', form);
    if (!variantInput) return;

    const original = buyBtn.textContent;
    buyBtn.disabled = true;
    buyBtn.innerHTML = '<span class="spinner spinner--white"></span>';

    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantInput.value, 10), quantity: 1 })
      });
      window.location.href = '/checkout';
    } catch (err) {
      buyBtn.textContent = 'Error';
      setTimeout(() => { buyBtn.textContent = original; buyBtn.disabled = false; }, 2000);
    }
  });
}

/* ── Scroll Animations (IntersectionObserver) ───────────── */
function initScrollAnimations() {
  const elements = rw.qsa('.fade-up, .fade-in');
  if (!elements.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  elements.forEach(el => observer.observe(el));
}

/* ── Accordion ──────────────────────────────────────────── */
function initAccordion() {
  rw.qsa('.accordion-trigger').forEach(trigger => {
    rw.on(trigger, 'click', () => {
      const body = trigger.nextElementSibling;
      const expanded = trigger.getAttribute('aria-expanded') === 'true';

      // Close all
      rw.qsa('.accordion-trigger').forEach(t => {
        t.setAttribute('aria-expanded', 'false');
        const b = t.nextElementSibling;
        if (b) b.style.maxHeight = '0';
      });

      // Open clicked
      if (!expanded) {
        trigger.setAttribute('aria-expanded', 'true');
        if (body) body.style.maxHeight = body.scrollHeight + 'px';
      }
    });
  });
}

/* ── Marquee Pause-on-Hover ─────────────────────────────── */
function initMarquee() {
  const tracks = rw.qsa('.marquee-track');
  tracks.forEach(track => {
    rw.on(track, 'mouseenter', () => {
      track.style.animationPlayState = 'paused';
    });
    rw.on(track, 'mouseleave', () => {
      track.style.animationPlayState = 'running';
    });
  });
}

/* ── Main Init ──────────────────────────────────────────── */
function init() {
  initHeader();
  initHamburger();
  initSearch();
  CartDrawer.init();
  initQuickAdd();
  initProductPage();
  initScrollAnimations();
  initAccordion();
  initMarquee();
}

// Anti-white-screen: use DOMContentLoaded + shopify:section:load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', (e) => {
  initHeader();
  initHamburger();
  initSearch();
  initQuickAdd();
  initScrollAnimations();
  initAccordion();
  initMarquee();
  CartDrawer.init();
  initProductPage();
});
